import React, { useState, useEffect } from 'react';
import {
    FaUser,
    FaSearch,
    FaSpinner,
    FaChevronLeft,
    FaChevronRight,
    FaHistory,
    FaCreditCard,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimes,
    FaEnvelope,
    FaPhone,
    FaBuilding,
    FaSync,
    FaCog
} from 'react-icons/fa';
import itCustomerService from '../services/itCustomerService';

const ITServicesCustomerManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await itCustomerService.listCustomers();
            setCustomers(data);
        } catch (err) {
            setError('Failed to load customers');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomers = customers.filter(cust =>
        cust.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cust.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cust.companyName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">IT Services Customers</h1>
                <p className="text-[#a89984]">Manage IT service clients, view active recurring services, and billing history</p>
            </div>

            <div className="bg-[#3c3836] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 md:flex-initial">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a89984]" />
                    <input
                        type="text"
                        placeholder="Search IT customers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700] w-full md:w-80"
                    />
                </div>
                <button
                    onClick={loadCustomers}
                    className="flex items-center space-x-2 bg-[#504945] text-[#ebdbb2] px-4 py-2 rounded-lg hover:bg-[#665c54] transition-all"
                >
                    <FaSync className={loading ? 'animate-spin' : ''} />
                    <span>Refresh</span>
                </button>
            </div>

            <div className="bg-[#3c3836] rounded-lg overflow-hidden border border-[#504945]">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-[#282828]">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase">Customer</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase">Services</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase">Stripe Link</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase">Joined</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#ebdbb2] uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#504945]">
                            {paginatedCustomers.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-[#a89984]">No IT customers found</td>
                                </tr>
                            ) : (
                                paginatedCustomers.map((customer) => (
                                    <tr key={customer.customerId} className="hover:bg-[#504945] transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 mr-3">
                                                    <FaUser />
                                                </div>
                                                <div>
                                                    <div className="text-[#ebdbb2] font-semibold">{customer.customerName}</div>
                                                    <div className="text-[#a89984] text-xs">{customer.email}</div>
                                                    {customer.companyName && <div className="text-yellow-500/80 text-[10px] uppercase font-bold">{customer.companyName}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 rounded bg-green-500/20 text-green-300 text-xs font-medium">
                                                {customer.services?.length || 0} Active
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {customer.stripeCustomerId ? (
                                                <span className="text-blue-300 text-xs font-mono">{customer.stripeCustomerId}</span>
                                            ) : (
                                                <span className="text-[#a89984] text-xs italic">Not linked</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#ebdbb2]">
                                            {formatDate(customer.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 text-right space-x-2">
                                            <button
                                                onClick={() => { setSelectedCustomer(customer); setShowViewModal(true); }}
                                                className="p-2 bg-[#282828] text-[#ebdbb2] rounded hover:bg-[#FFD700] hover:text-[#1d2021] transition-all"
                                                title="View Details"
                                            >
                                                <FaHistory />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Modal */}
            {showViewModal && selectedCustomer && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 animate-fadeIn">
                    <div className="bg-[#282828] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-[#3c3836]">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-[#3c3836] flex justify-between items-center bg-[#1d2021]">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-400">
                                    <FaBuilding size={24} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-[#ebdbb2]">{selectedCustomer.customerName}</h2>
                                    <p className="text-[#a89984] text-sm">{selectedCustomer.companyName || 'Private Client'}</p>
                                </div>
                            </div>
                            <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-[#3c3836] rounded-full transition-colors">
                                <FaTimes size={20} className="text-[#a89984]" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Sidebar Info */}
                            <div className="space-y-4">
                                <div className="bg-[#1d2021] p-4 rounded-xl border border-[#3c3836]">
                                    <h3 className="text-[#a89984] text-xs font-bold uppercase mb-3 px-1">Contact Info</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center text-[#ebdbb2] text-sm">
                                            <FaEnvelope className="mr-3 text-orange-400/70" />
                                            <span className="truncate">{selectedCustomer.email}</span>
                                        </div>
                                        {selectedCustomer.phone && (
                                            <div className="flex items-center text-[#ebdbb2] text-sm">
                                                <FaPhone className="mr-3 text-orange-400/70" />
                                                <span>{selectedCustomer.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-[#1d2021] p-4 rounded-xl border border-[#3c3836]">
                                    <h3 className="text-[#a89984] text-xs font-bold uppercase mb-3 px-1">Payment Status</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-[#a89984]">Method:</span>
                                            <span className="text-blue-400 flex items-center">
                                                <FaCreditCard className="mr-2" />
                                                {selectedCustomer.paymentMethodId ? 'Saved Card' : 'No Method'}
                                            </span>
                                        </div>
                                        {selectedCustomer.stripeCustomerId && (
                                            <a
                                                href={`https://dashboard.stripe.com/customers/${selectedCustomer.stripeCustomerId}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="block text-center py-2 bg-indigo-600/20 text-indigo-400 rounded-lg text-xs font-bold hover:bg-indigo-600/40 transition-all border border-indigo-500/30"
                                            >
                                                Open in Stripe Dashboard
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Main Content - Services */}
                            <div className="md:col-span-2 space-y-6">
                                <div>
                                    <h3 className="text-xl font-bold text-[#ebdbb2] mb-4 flex items-center">
                                        <FaCog className="mr-2 text-[#FFD700]" />
                                        Active Services & Subscriptions
                                    </h3>

                                    {(!selectedCustomer.services || selectedCustomer.services.length === 0) ? (
                                        <div className="bg-[#1d2021] p-8 rounded-2xl border border-dashed border-[#3c3836] text-center">
                                            <FaExclamationTriangle className="mx-auto text-yellow-500/50 text-3xl mb-3" />
                                            <p className="text-[#a89984]">No active recurring services found for this customer.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {selectedCustomer.services.map((svc, idx) => (
                                                <div key={idx} className="bg-[#1d2021] p-5 rounded-2xl border border-[#3c3836] hover:border-orange-500/50 transition-all group">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="text-[#ebdbb2] font-bold text-lg group-hover:text-orange-400 transition-colors">{svc.name}</h4>
                                                            <p className="text-[#a89984] text-xs font-mono mb-3">{svc.serviceId} • {svc.frequency || 'monthly'}</p>

                                                            <div className="flex items-center space-x-4">
                                                                <div className="text-[#ebdbb2]">
                                                                    <span className="text-[#a89984] text-[10px] block uppercase font-bold">Billing Amount</span>
                                                                    <span className="text-xl text-[#FFD700] font-bold">{formatCurrency(svc.amount)}</span>
                                                                </div>
                                                                <div className="h-8 w-px bg-[#3c3836]"></div>
                                                                <div>
                                                                    <span className="text-[#a89984] text-[10px] block uppercase font-bold">Next Billing</span>
                                                                    <span className="text-[#ebdbb2] font-medium">{formatDate(svc.nextBillingDate)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wider">
                                                            {svc.status || 'Active'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ITServicesCustomerManagement;
